import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth, parseISO } from 'date-fns';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';

const EVENT_STYLES = {
  // (border color, background+text)
  demo:              { lane: 'demo',            dot: 'bg-purple-500',  pill: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',  border: 'border-l-purple-500',  label: 'Demo' },
  poc:               { lane: 'poc',             dot: 'bg-indigo-500',  pill: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',  border: 'border-l-indigo-500',  label: 'POC' },
  technical_evaluation: { lane: 'tech',         dot: 'bg-cyan-500',    pill: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',         border: 'border-l-cyan-500',    label: 'Tech Eval' },
  solution_design:   { lane: 'design',          dot: 'bg-teal-500',    pill: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',         border: 'border-l-teal-500',    label: 'Design' },
  integration_assessment: { lane: 'integration', dot: 'bg-emerald-500', pill: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', border: 'border-l-emerald-500', label: 'Integration' },
  customer_meeting:  { lane: 'meeting',         dot: 'bg-blue-500',    pill: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',         border: 'border-l-blue-500',    label: 'Meeting' },
  meeting:           { lane: 'meeting',         dot: 'bg-blue-500',    pill: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',         border: 'border-l-blue-500',    label: 'Meeting' },
  outlook_event:     { lane: 'outlook',         dot: 'bg-orange-500',  pill: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',  border: 'border-l-orange-500',  label: 'Outlook' },
};
const DEFAULT_STYLE =  { lane: 'other',  dot: 'bg-slate-400', pill: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200', border: 'border-l-slate-400', label: 'Event' };

export default function PreSalesCalendar() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [laneFilter, setLaneFilter] = useState('all'); // all | presales | meeting

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      // Ask the backend for a wider window (full current + previous + next month)
      const start = format(subMonths(startOfMonth(cursor), 1), "yyyy-MM-dd'T'00:00:00");
      const end = format(addMonths(endOfMonth(cursor), 1), "yyyy-MM-dd'T'23:59:59");
      const res = await axios.get(`${API}/presales/calendar`, { params: { start, end } });
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Real-time push: when Microsoft Graph notifies us of a calendar change,
  // re-pull the month grid so the new/updated/deleted event appears instantly.
  // 30s-scale polling is not in place for this page — WS is the ONLY live path,
  // so no fallback fetch interval is started here (polling would flood the API).
  useRealtimeNotifications((evt) => {
    if (evt?.type === 'graph_change' && evt?.kind === 'calendar') {
      fetchCalendar();
    }
  });

  // Build the 6-row month grid
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const filtered = events.filter(ev => {
    if (laneFilter === 'all') return true;
    if (laneFilter === 'presales') return ev.source === 'presales';
    if (laneFilter === 'meeting') return ev.source === 'meeting' || ev.event_type === 'meeting';
    if (laneFilter === 'task') return ev.source === 'task';
    if (laneFilter === 'outlook') return ev.source === 'outlook';
    return true;
  });
  const byDay = filtered.reduce((m, ev) => {
    try {
      const d = parseISO(ev.datetime);
      const key = format(d, 'yyyy-MM-dd');
      (m[key] = m[key] || []).push(ev);
    } catch {}
    return m;
  }, {});

  // KPIs for the current month view
  const monthItems = filtered.filter(ev => {
    try { return isSameMonth(parseISO(ev.datetime), cursor); } catch { return false; }
  });
  const kpis = {
    total: monthItems.length,
    presales: monthItems.filter(e => e.source === 'presales').length,
    meetings: monthItems.filter(e => e.source === 'meeting').length,
    tasks: monthItems.filter(e => e.source === 'task').length,
    outlook: monthItems.filter(e => e.source === 'outlook').length,
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="presales-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Calendar</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Your unified schedule: demos · POCs · customer meetings · scheduled tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={laneFilter} onChange={e => setLaneFilter(e.target.value)} className="px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="calendar-lane-filter">
            <option value="all">All lanes</option>
            <option value="presales">Pre-Sales only</option>
            <option value="meeting">Customer Meetings only</option>
            <option value="task">Tasks only</option>
            <option value="outlook">Outlook only</option>
          </select>
          <button onClick={() => setCursor(subMonths(cursor, 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="cal-prev" aria-label="Previous month">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <div className="px-3 py-2 text-sm font-semibold text-slate-900 dark:text-white min-w-[140px] text-center" data-testid="cal-month-label">{format(cursor, 'MMMM yyyy')}</div>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="cal-next" aria-label="Next month">
            <svg className="w-5 h-5 text-slate-600 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="cal-today">Today</button>
        </div>
      </div>

      {/* Month KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="This Month" value={kpis.total} testId="cal-kpi-total" />
        <KPI label="Pre-Sales Requests" value={kpis.presales} accent="text-purple-600 dark:text-purple-400" testId="cal-kpi-presales" />
        <KPI label="Customer Meetings" value={kpis.meetings} accent="text-blue-600 dark:text-blue-400" testId="cal-kpi-meetings" />
        <KPI label="Scheduled Tasks" value={kpis.tasks} accent="text-slate-700 dark:text-slate-300" testId="cal-kpi-tasks" />
        <KPI label="Outlook Events" value={kpis.outlook} accent="text-orange-600 dark:text-orange-400" testId="cal-kpi-outlook" />
      </div>

      {/* Calendar Grid */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
            <div key={d} className="px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide text-center">{d}</div>
          ))}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-7 grid-rows-6 min-h-[720px]">
            {days.map((day, i) => {
              const key = format(day, 'yyyy-MM-dd');
              const items = byDay[key] || [];
              const isOtherMonth = !isSameMonth(day, cursor);
              const isToday = isSameDay(day, new Date());
              return (
                <div key={i} className={`border-b border-r border-slate-200 dark:border-slate-700 p-1.5 flex flex-col gap-1 overflow-hidden min-h-[120px] ${isOtherMonth ? 'bg-slate-50 dark:bg-slate-900/20' : 'bg-white dark:bg-slate-800'}`} data-testid={`cal-cell-${key}`}>
                  <div className={`text-xs font-semibold ${isToday ? 'inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white rounded-full' : isOtherMonth ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {items.slice(0, 5).map(ev => {
                      const s = EVENT_STYLES[ev.event_type] || DEFAULT_STYLE;
                      let time = '';
                      try { time = format(parseISO(ev.datetime), 'HH:mm'); } catch {}
                      const isExternal = ev.source === 'outlook' && ev.deep_link && ev.deep_link.startsWith('http');
                      const handleClick = () => {
                        if (!ev.deep_link) return;
                        if (isExternal) {
                          window.open(ev.deep_link, '_blank', 'noopener,noreferrer');
                        } else {
                          navigate(ev.deep_link);
                        }
                      };
                      // Best-available human title for the event pill.
                      const titleText = ev.title || ev.subject || ev.lead_name || ev.organizer || s.label;
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={handleClick}
                          title={`${s.label} • ${titleText}${ev.lead_name && ev.lead_name !== titleText ? ` • ${ev.lead_name}` : ''}${ev.organizer && ev.organizer !== titleText ? ` • ${ev.organizer}` : ''}${time ? ` • ${time}` : ''}${isExternal ? ' • opens in Outlook' : ''}`}
                          className={`truncate text-[10px] pl-1.5 pr-1 py-0.5 rounded text-left transition-opacity hover:opacity-80 border-l-2 ${s.border} ${s.pill}`}
                          data-testid={`cal-event-${ev.id}`}
                        >
                          {time && <span className="font-semibold mr-1 tabular-nums">{time}</span>}
                          <span className="font-medium">{titleText}</span>
                        </button>
                      );
                    })}
                    {items.length > 5 && (
                      <div className="text-[10px] text-slate-500 px-1.5">+{items.length - 5} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-xs text-slate-500 dark:text-slate-400" data-testid="cal-legend">
        <span className="font-medium">Lanes:</span>
        {[
          ['customer_meeting', 'Customer Meeting'],
          ['demo', 'Demo'],
          ['poc', 'POC'],
          ['technical_evaluation', 'Tech Eval'],
          ['solution_design', 'Design'],
          ['integration_assessment', 'Integration'],
          ['outlook_event', 'Outlook'],
        ].map(([k, label]) => {
          const s = EVENT_STYLES[k] || DEFAULT_STYLE;
          return (
            <span key={k} className="flex items-center gap-1">
              <span className={`w-3 h-3 rounded ${s.dot}`}></span>{label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const KPI = ({ label, value, accent = 'text-slate-900 dark:text-white', testId }) => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid={testId}>
    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);
